import React from 'react';
import { Platform, Pressable, StyleProp, TextInput, View, ViewStyle } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { ActivityIndicator } from 'react-native-paper';
import { RADIUS, shadow } from '../../assets/theme';
import { makeStyles, useAppTheme } from '../../theme/ThemeContext';

type SearchPillProps = {
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  loading?: boolean;
  style?: StyleProp<ViewStyle>;
  autoFocus?: boolean;
  /** Optional element rendered inside the pill on the right (e.g. a filter toggle). */
  trailing?: React.ReactNode;
};

/** Airbnb-style rounded search bar with a coral search badge. */
export default function SearchPill({
  value,
  onChangeText,
  placeholder = 'Rechercher…',
  loading,
  style,
  autoFocus,
  trailing,
}: SearchPillProps) {
  const styles = useStyles();
  const { colors } = useAppTheme();

  return (
    <View style={[styles.pill, shadow(colors, 2), style]}>
      <View style={styles.iconBadge}>
        {loading ? (
          <ActivityIndicator size={14} color={colors.onPrimary} />
        ) : (
          <MaterialCommunityIcons name="magnify" size={18} color={colors.onPrimary} />
        )}
      </View>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={colors.textMuted}
        style={styles.input}
        autoFocus={autoFocus}
        autoCapitalize="none"
        autoCorrect={false}
        returnKeyType="search"
        accessibilityLabel={placeholder}
      />
      {value ? (
        <Pressable
          onPress={() => onChangeText('')}
          accessibilityRole="button"
          accessibilityLabel="Effacer la recherche"
          hitSlop={8}
          style={styles.clear}
        >
          <MaterialCommunityIcons name="close" size={16} color={colors.textPrimary} />
        </Pressable>
      ) : null}
      {trailing}
    </View>
  );
}

const useStyles = makeStyles((c) => ({
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 52,
    borderRadius: RADIUS.pill,
    borderWidth: 1,
    borderColor: c.border,
    backgroundColor: c.surface,
    paddingLeft: 8,
    paddingRight: 8,
    gap: 8,
  },
  iconBadge: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: c.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  input: {
    flex: 1,
    minWidth: 0,
    fontSize: 15,
    color: c.textPrimary,
    paddingVertical: 12,
    // Remove the default browser focus ring; the pill itself is the affordance.
    ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as object) : null),
  },
  clear: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: c.surfaceAlt,
    alignItems: 'center',
    justifyContent: 'center',
  },
}));
